/**
 * CrawlIntelligenceDashboard
 *
 * Displays crawl intelligence metrics - bot traffic, AEO exposure funnel,
 * traffic trends, and URL-level deployment status.
 *
 * Story 56: View crawl intelligence metrics in admin dashboard
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { useSite } from '@/contexts/SiteContext';
import { type BotUniqueUrlsSemantics } from './crawlIntelligence/botUniqueUrlsLabel';
import { CaptureScopeNotesBanner, type CaptureScopeNotesMeta } from './crawlIntelligence/CaptureScopeNotesBanner';
import { BotCrawlTableHeaderRow } from './crawlIntelligence/BotCrawlTableHeaderRow';
import { BotUniqueUrlsCell } from './crawlIntelligence/BotUniqueUrlsCell';
import { BotReqPerUrlCell } from './crawlIntelligence/BotReqPerUrlCell';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { AlertCircle, Bot, Users, FileCheck, TrendingUp, Clock, HardDrive, Activity, BarChart3, Eye, EyeOff, Target, AlertTriangle, RefreshCw, Layers, ChevronDown, ChevronRight, ExternalLink, CheckCircle2, Cpu, Globe, Search, Sparkles } from 'lucide-react';
import { BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';

// Visibility Diagnosis Tab (Story 63)
import { VisibilityDiagnosisTab } from '@/components/visibility-diagnosis';

// AEO Priorities Tab
import { AeoPrioritiesTab } from '@/components/aeo-priorities';

// Bots diario (3A attribution) — gated by impact-products availability
import { BotsDiarioTab } from '@/components/impact/BotsDiarioTab';
import {
  impactDashboardService,
  ImpactDashboardError,
  type BotsDashboardData,
} from '@/services/impactDashboardService';
import { useImpactAvailability } from '@/hooks/useImpactAvailability';

// Bot colors for consistent identification
const BOT_COLORS: Record<string, string> = {
  googlebot: '#4285F4',
  bingbot: '#00809D',
  gptbot: '#10A37F',
  claudebot: '#D97706',
  yandex: '#FF0000',
  facebook: '#1877F2',
  applebot: '#A2AAAD',
  moz: '#2B5797',
  semrush: '#FF642D',
  ahrefs: '#0066FF',
  amazonbot: '#FF9900',
  default: '#6B7280',
};

const getBotColor = (botName: string): string => {
  const normalized = botName.toLowerCase();
  for (const [key, color] of Object.entries(BOT_COLORS)) {
    if (normalized.includes(key)) return color;
  }
  return BOT_COLORS.default;
};

interface FunnelData {
  potential_urls: number;
  deployed_urls: number;
  deployment_rate: number;
  total_bot_requests: number;
  total_human_requests: number;
  potential_bot_visits: number;
  deployed_bot_visits: number;
  bot_exposure_rate: number;
  potential_human_visits: number;
  deployed_human_visits: number;
}

interface TimeSeriesPoint {
  date: string;
  human_requests: number;
  bot_requests: number;
  bot_bytes: number;
  bots_by_type?: Record<string, number>;
}

interface BotBreakdown {
  bot_name: string;
  requests: number;
  bytes: number;
  unique_urls: number;
  // per-value qualifiers (task-sl8e2nlcpo): an HLL estimate with its error, or a capped count
  unique_urls_estimate?: boolean;
  unique_urls_rse?: number | null;
  unique_urls_saturated?: boolean;
  unique_urls_scope?: 'day' | 'run' | null;
  unique_urls_capped_part?: number;
  unique_urls_estimated_part?: number;
  unique_urls_peak_part?: number;
  unique_urls_rse_known?: boolean;
  unique_urls_estimate_basis?: number;
}

interface TopUrl {
  url_path: string;
  human_visits: number;
  bot_visits: number;
  last_bot_visit: string | null;
  is_potential: boolean;
  is_deployed: boolean;
}

interface AnalyticsData {
  time_series: TimeSeriesPoint[];
  bot_breakdown: BotBreakdown[];
  // which bound bot_breakdown[].unique_urls is (PR #5924); absent on a backend older than that PR.
  // capture_scope_notes: what the rows in the window CONTAIN (task-fm66ezuxve); the banner reads
  // the same object from the /summary envelope's meta
  semantics?: { bot_unique_urls: BotUniqueUrlsSemantics; capture_scope_notes?: CaptureScopeNotesMeta };
  top_urls: TopUrl[];
  status_codes_by_bot: { [bot: string]: { [code: string]: number } };
  totals: {
    human_requests: number;
    peak_unique_visitors: number;
    peak_unique_human_urls: number;
    days_with_data: number;
    url_cap_saturated?: boolean;
    ip_cap_saturated?: boolean;
  };
  bot_time_series?: { [bot: string]: { date: string; requests: number; bytes: number }[] };
}

interface OrganicDailyData {
  date: string;
  google: number;
  bing: number;
  search_total: number;
  perplexity: number;
  chatgpt: number;
  claude: number;
  ai_total: number;
  direct: number;
  referral: number;
  human_total: number;
}

interface OrganicTrafficData {
  daily: OrganicDailyData[];
  totals: {
    google: number;
    bing: number;
    search_total: number;
    perplexity: number;
    chatgpt: number;
    claude: number;
    ai_total: number;
    direct: number;
    referral: number;
    human_total: number;
  };
  aeo_impact: {
    total_organic: number;
    deployed_organic: number;
    pct_on_enriched: number;
    deployed_urls: number;
  };
  // backend-merged/services/CrawlMetricsService.js getOrganicTrafficTrends: both null when no organic rows exist
  freshness: {
    last_data_date: string | null;
    last_updated: string | null;
  };
}

interface BotMetrics {
  [botName: string]: {
    requests: number;
    bytes?: number;
    unique_urls?: number;
    status_codes?: { [code: string]: number };
  };
}

interface SummaryData {
  date: string;
  bot_metrics: BotMetrics;
  human_requests: number;
  human_unique_visitors: number;
  potential_urls: number;
  deployed_urls: number;
  total_bot_requests?: number;
  total_bot_bytes?: number;
}

interface UrlMetric {
  url_path: string;
  human_visits: number;
  total_bot_visits: number;
  is_potential: boolean;
  is_deployed: boolean;
  last_bot_visit: string | null;
}

interface WallData {
  type: string;
  severity: string;
  location?: string;
  evidence: string;
  products_blocked: number;
  recommendation: string;
}

interface ActionItem {
  title: string;
  description: string;
  type?: string;
  code?: string;
  link?: string;
}

interface RecommendationData {
  priority: number;
  category: string;
  title: string;
  description: string;
  impact: string;
  actions?: ActionItem[];
  sample_urls?: string[];
}

interface BotCoverageItem {
  name: string;
  display_name: string;
  urls_crawled: number;
  total_visits: number;
  max_depth: number;
  avg_depth: number;
  coverage_pct: number;
  aeo_urls_crawled: number;
  aeo_coverage_pct: number;
  is_ai_bot: boolean;
  status: 'healthy' | 'warning' | 'critical';
}

interface BotCoverageData {
  search_bots: BotCoverageItem[];
  ai_bots: BotCoverageItem[];
  other_bots: BotCoverageItem[];
  total_urls: number;
  aeo_total: number;
}

// Discovery insights interfaces
interface BotEntryPoint {
  url_path: string;
  page_type: string;
  url_depth: number;
  session_count: number;
  pct_of_sessions: number;
}

interface BotEntryPointData {
  bot_name: string;
  display_name: string;
  total_sessions: number;
  entry_points: BotEntryPoint[];
  patterns: {
    homepage_pct: number;
    product_pct: number;
    category_pct: number;
    other_pct: number;
    avg_entry_depth: number;
  };
}

interface BotDepthData {
  depth: number;
  unique_urls: number;
  total_visits: number;
  pct_of_urls: number;
  pct_of_visits: number;
}

interface BotDepthDropoff {
  bot_name: string;
  display_name: string;
  max_depth: number;
  total_urls: number;
  total_visits: number;
  depth_distribution: BotDepthData[];
  dropoff_point: {
    depth: number;
    drop_pct: number;
    visits_before: number;
    visits_after: number;
  } | null;
  insight: string;
}

interface BotDiscoveryGap {
  bot_name: string;
  display_name: string;
  gaps_by_type: {
    [type: string]: {
      total_urls: number;
      undiscovered_urls: number;
      gap_pct: number;
      sample_urls: string[];
    };
  };
  gaps_by_depth: {
    [depth: string]: {
      total_urls: number;
      undiscovered_urls: number;
      gap_pct: number;
    };
  };
  total_undiscovered: number;
  worst_gap: {
    type: string;
    depth: number;
    gap_pct: number;
    undiscovered: number;
  } | null;
}

interface FrontierData {
  catalog_coverage: {
    total_products: number;
    products_in_sitemap: number;
    products_crawled_30d: number;
    catalog_coverage_pct: number;
    aeo_products_total: number;
    aeo_products_crawled: number;
    aeo_coverage_pct: number;
  };
  depth_analysis: {
    max_crawl_depth: number;
    avg_crawl_depth: number;
    depth_distribution: { [depth: string]: { urls: number; visits: number } };
    products_beyond_depth_3: number;
    products_beyond_depth_3_crawled: number;
  };
  walls_detected: WallData[];
  recommendations: RecommendationData[];
  last_analysis: string | null;
}

export default function CrawlIntelligenceDashboard() {
  const { selectedSite } = useSite();
  const [loading, setLoading] = useState(true);
  const [funnel, setFunnel] = useState<FunnelData | null>(null);
  const [summary, setSummary] = useState<SummaryData[]>([]);
  // Tied to the site the answer is FOR (codex on PR #6010: a banner must never outlive a site
  // switch, and a failed summary must show as unavailable, never as the previous site's notes).
  // null until the summary answers; meta undefined on a backend older than task-fm66ezuxve.
  const [captureScopeNotes, setCaptureScopeNotes] = useState<{ siteId: string; meta: CaptureScopeNotesMeta | undefined } | null>(null);
  // bumped on every fetchData; a response from an older generation is obsolete and writes nothing
  const fetchGeneration = useRef(0);
  // per-panel failures for the site the answer is FOR (codex round 4 on PR #6010): a failed
  // funnel or analytics request is told to the reader, never left as the previous site's data
  const [panelErrors, setPanelErrors] = useState<{ siteId: string; funnel?: string; analytics?: string } | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [urls, setUrls] = useState<UrlMetric[]>([]);
  const [frontier, setFrontier] = useState<FrontierData | null>(null);
  const [frontierLoading, setFrontierLoading] = useState(false);
  const [botCoverage, setBotCoverage] = useState<BotCoverageData | null>(null);
  const [botEntryPoints, setBotEntryPoints] = useState<BotEntryPointData[]>([]);
  const [botDepthDropoff, setBotDepthDropoff] = useState<BotDepthDropoff[]>([]);
  const [botDiscoveryGaps, setBotDiscoveryGaps] = useState<BotDiscoveryGap[]>([]);
  const [selectedBot, setSelectedBot] = useState<string | null>(null);
  const [expandedRecs, setExpandedRecs] = useState<Set<number>>(new Set());
  const [expandedBots, setExpandedBots] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState('overview');
  const [urlFilter, setUrlFilter] = useState<'all' | 'potential' | 'deployed'>('all');
  const [enabledBots, setEnabledBots] = useState<Set<string>>(new Set());
  const [organicData, setOrganicData] = useState<OrganicTrafficData | null>(null);
  const [organicLoading, setOrganicLoading] = useState(false);

  const siteId = selectedSite?.id;

  // Bots diario (3A attribution) — gated by availability check
  const { available: productAvailable, error: availabilityError } = useImpactAvailability(siteId);
  const [botsData, setBotsData] = useState<BotsDashboardData | null>(null);
  const [botsError, setBotsError] = useState<string | null>(null);
  const [botsLoading, setBotsLoading] = useState(false);
  const botsVersion = useRef(0);

  useEffect(() => {
    if (selectedSite) {
      fetchData();
    }
  }, [selectedSite]);

  useEffect(() => {
    if (selectedSite && activeTab === 'urls') {
      fetchUrls();
    }
  }, [selectedSite, urlFilter, activeTab]);

  useEffect(() => {
    if (selectedSite && activeTab === 'frontier') {
      fetchFrontier();
    }
  }, [selectedSite, activeTab]);

  useEffect(() => {
    if (selectedSite && activeTab === 'organic') {
      fetchOrganicTraffic();
    }
  }, [selectedSite, activeTab]);

  // Reset activeTab when bots-diario becomes unavailable
  useEffect(() => {
    if (productAvailable === false) {
      setActiveTab(prev => prev === 'bots-diario' ? 'overview' : prev);
    }
  }, [productAvailable]);

  // Clear bots state on site change
  useEffect(() => {
    botsVersion.current += 1;
    setBotsData(null);
    setBotsError(null);
    setBotsLoading(false);
  }, [siteId]);

  // Fetch bots snapshot when bots-diario tab is activated
  useEffect(() => {
    if (activeTab !== 'bots-diario' || !siteId) return;
    const v = ++botsVersion.current;
    setBotsLoading(true);
    setBotsError(null);
    impactDashboardService.get(siteId, 'bots')
      .then((result) => {
        if (botsVersion.current !== v) return;
        setBotsData(result as BotsDashboardData);
      })
      .catch((err) => {
        if (botsVersion.current !== v) return;
        setBotsData(null);
        setBotsError(err instanceof ImpactDashboardError ? err.message : 'Error loading the bots report');
      })
      .finally(() => {
        if (botsVersion.current === v) setBotsLoading(false);
      });
  }, [activeTab, siteId]);

  // Initialize enabled bots when analytics loads
  useEffect(() => {
    if (analytics?.bot_breakdown) {
      const topBots = analytics.bot_breakdown.slice(0, 5).map(b => b.bot_name);
      setEnabledBots(new Set(topBots));
    }
  }, [analytics?.bot_breakdown]);

  const fetchData = async () => {
    if (!selectedSite) return;
    const siteId = selectedSite.id;
    // Request generation (codex round 2 on PR #6010): a response that lands AFTER a newer fetch
    // started is obsolete and writes nothing, on the success and the failure path alike. Without
    // this, site A's slow answer arriving after bikeinn's notes rendered overwrote them, and the
    // render-time site check then hid bikeinn's estate-wide banner entirely.
    const generation = ++fetchGeneration.current;
    const current = () => generation === fetchGeneration.current;
    const commit = (update: () => void) => { if (current()) update(); };
    setLoading(true);
    // nothing of the previous site survives a switch, not even while the new answer is in flight:
    // the notes, the three panels' data, and the panel failures (codex rounds 1 and 4)
    setCaptureScopeNotes(null);
    setFunnel(null);
    setSummary([]);
    setAnalytics(null);
    setPanelErrors(null);
    const notesUnavailable = (reason: string) =>
      commit(() => setCaptureScopeNotes({ siteId, meta: { available: false, reason, window: null, notes: [] } }));

    // The three requests are independent (codex round 3 on PR #6010): a funnel or analytics
    // failure, or a body that will not parse, must neither hide a successful summary nor be
    // reported as "the summary request failed". Each one is loaded and reported on its own.
    // Each panel owns its own lifecycle (codex rounds 3 and 4): its loader commits its data or
    // its failure THE MOMENT it settles, under the generation check, so a stalled or failed
    // sibling never delays or alters what the other panels show. The requests have no timeout,
    // so waiting on all three before reporting one would leave a known summary failure with no
    // banner for as long as a sibling hangs.
    const describe = (error: unknown) => (error instanceof Error ? error.message : String(error));
    const panelFailed = (panel: 'funnel' | 'analytics', error: unknown) => {
      console.error(`Failed to fetch crawl intelligence ${panel}:`, error);
      commit(() => setPanelErrors((prev) => ({ ...(prev?.siteId === siteId ? prev : {}), siteId, [panel]: describe(error) })));
    };
    const loadFunnel = async () => {
      try {
        const res = await authFetch(`${API_URL}/crawl-intelligence/funnel?siteId=${siteId}&days=30`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        commit(() => setFunnel(data.data));
      } catch (error) {
        panelFailed('funnel', error);
      }
    };
    const loadSummary = async () => {
      try {
        const res = await authFetch(`${API_URL}/crawl-intelligence/summary?siteId=${siteId}&days=30`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        commit(() => {
          setSummary(data.data || []);
          // what these rows contain, over the same window (task-fm66ezuxve); undefined on an older backend
          setCaptureScopeNotes({ siteId, meta: data.meta?.capture_scope_notes });
        });
      } catch (error) {
        console.error('Failed to fetch crawl intelligence summary:', error);
        notesUnavailable(`the summary request for this site failed (${describe(error)}), so its capture scope notes could not be loaded`);
      }
    };
    const loadAnalytics = async () => {
      try {
        const res = await authFetch(`${API_URL}/crawl-intelligence/analytics?siteId=${siteId}&days=30`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        commit(() => setAnalytics(data.data));
      } catch (error) {
        panelFailed('analytics', error);
      }
    };

    try {
      // every loader settles on its own; this only decides when the skeleton goes away
      await Promise.all([loadFunnel(), loadSummary(), loadAnalytics()]);
    } finally {
      // a superseded fetch leaves the loading flag to the fetch that superseded it
      commit(() => setLoading(false));
    }
  };

  const fetchUrls = async () => {
    if (!selectedSite) return;

    try {
      const filterParam = urlFilter !== 'all' ? `&filter=${urlFilter}` : '';
      const res = await authFetch(
        `${API_URL}/crawl-intelligence/urls?siteId=${selectedSite.id}&limit=100${filterParam}`
      );

      if (res.ok) {
        const data = await res.json();
        setUrls(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch URL metrics:', error);
    }
  };

  const fetchFrontier = async () => {
    if (!selectedSite) return;
    setFrontierLoading(true);

    try {
      // Fetch frontier data, bot coverage, and discovery insights in parallel
      const [frontierRes, botCoverageRes, entryPointsRes, depthDropoffRes, discoveryGapsRes] = await Promise.all([
        authFetch(`${API_URL}/crawl-intelligence/frontier?siteId=${selectedSite.id}`),
        authFetch(`${API_URL}/crawl-intelligence/bot-coverage?siteId=${selectedSite.id}`),
        authFetch(`${API_URL}/crawl-intelligence/bot-entry-points?siteId=${selectedSite.id}`),
        authFetch(`${API_URL}/crawl-intelligence/bot-depth-dropoff?siteId=${selectedSite.id}`),
        authFetch(`${API_URL}/crawl-intelligence/bot-discovery-gaps?siteId=${selectedSite.id}`)
      ]);

      if (frontierRes.ok) {
        const data = await frontierRes.json();
        setFrontier(data.data);
      }

      if (botCoverageRes.ok) {
        const data = await botCoverageRes.json();
        setBotCoverage(data.data);
      }

      if (entryPointsRes.ok) {
        const data = await entryPointsRes.json();
        setBotEntryPoints(data.data || []);
      }

      if (depthDropoffRes.ok) {
        const data = await depthDropoffRes.json();
        setBotDepthDropoff(data.data || []);
      }

      if (discoveryGapsRes.ok) {
        const data = await discoveryGapsRes.json();
        setBotDiscoveryGaps(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch frontier data:', error);
    } finally {
      setFrontierLoading(false);
    }
  };

  const fetchOrganicTraffic = async () => {
    if (!selectedSite) return;
    setOrganicLoading(true);

    try {
      const res = await authFetch(
        `${API_URL}/crawl-intelligence/organic-traffic?siteId=${selectedSite.id}&days=30`
      );

      if (res.ok) {
        const data = await res.json();
        setOrganicData(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch organic traffic data:', error);
    } finally {
      setOrganicLoading(false);
    }
  };

  const runFrontierAnalysis = async () => {
    if (!selectedSite) return;
    setFrontierLoading(true);

    try {
      const res = await authFetch(`${API_URL}/crawl-intelligence/frontier/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ siteId: selectedSite.id })
      });

      if (res.ok) {
        const data = await res.json();
        setFrontier(data.data);
      }
    } catch (error) {
      console.error('Failed to run frontier analysis:', error);
    } finally {
      setFrontierLoading(false);
    }
  };

  const formatNumber = (n: number): string => {
    if (n >= 1000000000) return `${(n / 1000000000).toFixed(1)}B`;
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toString();
  };

  const formatBytes = (bytes: number): string => {
    if (bytes >= 1099511627776) return `${(bytes / 1099511627776).toFixed(1)} TB`;
    if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
    if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${bytes} B`;
  };

  const formatTimeAgo = (dateStr: string | null): string => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    return 'Recently';
  };

  // Calculate bot crawl statistics
  const botCrawlStats = useMemo(() => {
    if (!analytics?.bot_breakdown) return [];

    return analytics.bot_breakdown.map(bot => {
      const avgBytesPerRequest = bot.requests > 0 ? bot.bytes / bot.requests : 0;
      const urlsPerVisit = bot.requests > 0 ? bot.unique_urls / Math.ceil(bot.requests / 100) : 0; // Estimate sessions as ~100 reqs
      const crawlEfficiency = bot.unique_urls > 0 ? (bot.requests / bot.unique_urls).toFixed(1) : '0';

      return {
        ...bot,
        avgBytesPerRequest,
        urlsPerVisit: Math.round(urlsPerVisit),
        crawlEfficiency,
        estimatedSessions: Math.ceil(bot.requests / 100),
      };
    });
  }, [analytics?.bot_breakdown]);

  // Calculate max bandwidth for bar scaling
  const maxBandwidth = useMemo(() => {
    if (!analytics?.bot_breakdown?.length) return 1;
    return Math.max(...analytics.bot_breakdown.map(b => b.bytes));
  }, [analytics?.bot_breakdown]);

  // Toggle bot visibility
  const toggleBot = (botName: string) => {
    setEnabledBots(prev => {
      const next = new Set(prev);
      if (next.has(botName)) {
        next.delete(botName);
      } else {
        next.add(botName);
      }
      return next;
    });
  };

  // SVG Line Chart Component for Bot Traffic
  const BotLineChart = ({ data, bots }: { data: TimeSeriesPoint[], bots: BotBreakdown[] }) => {
    const width = 800;
    const height = 300;
    const padding = { top: 20, right: 20, bottom: 40, left: 60 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    // Get per-bot data from summary, aggregated by date
    const botData = useMemo(() => {
      const result: Record<string, { date: string; requests: number }[]> = {};
      // Temporary map to aggregate by date: botName -> date -> total requests
      const aggregated: Record<string, Record<string, number>> = {};

      // Initialize for top bots
      bots.slice(0, 10).forEach(bot => {
        result[bot.bot_name] = [];
        aggregated[bot.bot_name] = {};
      });

      // Aggregate requests by date (handles multiple entries for same day)
      summary.forEach(day => {
        if (day.bot_metrics) {
          const dateKey = day.date.split('T')[0]; // Normalize to YYYY-MM-DD
          Object.entries(day.bot_metrics).forEach(([botName, metrics]) => {
            if (aggregated[botName] !== undefined) {
              aggregated[botName][dateKey] = (aggregated[botName][dateKey] || 0) + (metrics.requests || 0);
            }
          });
        }
      });

      // Convert aggregated data to sorted array format
      Object.entries(aggregated).forEach(([botName, dateMap]) => {
        result[botName] = Object.entries(dateMap)
          .map(([date, requests]) => ({ date, requests }))
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      });

      return result;
    }, [summary, bots]);

    // Calculate scales
    const dates = data.map(d => new Date(d.date));
    const minDate = Math.min(...dates.map(d => d.getTime()));
    const maxDate = Math.max(...dates.map(d => d.getTime()));

    let maxRequests = 0;
    Object.entries(botData).forEach(([botName, points]) => {
      if (enabledBots.has(botName)) {
        points.forEach(p => {
          if (p.requests > maxRequests) maxRequests = p.requests;
        });
      }
    });
    if (maxRequests === 0) maxRequests = 1;

    const xScale = (date: Date) => {
      const range = maxDate - minDate || 1;
      return padding.left + ((date.getTime() - minDate) / range) * chartWidth;
    };

    const yScale = (value: number) => {
      return padding.top + chartHeight - (value / maxRequests) * chartHeight;
    };

    // Generate path for each bot
    const generatePath = (points: { date: string; requests: number }[]) => {
      if (points.length === 0) return '';
      const sorted = [...points].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      return sorted.map((p, i) => {
        const x = xScale(new Date(p.date));
        const y = yScale(p.requests);
        return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
      }).join(' ');
    };

    // Y-axis ticks
    const yTicks = [0, 0.25, 0.5, 0.75, 1].map(p => Math.round(maxRequests * p));

    return (
      <div className="w-full overflow-x-auto">
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ minWidth: '600px' }}>
          {/* Y-axis */}
          <line x1={padding.left} y1={padding.top} x2={padding.left} y2={padding.top + chartHeight} stroke="#e5e7eb" />
          {yTicks.map((tick, i) => (
            <g key={i}>
              <line
                x1={padding.left - 5}
                y1={yScale(tick)}
                x2={padding.left}
                y2={yScale(tick)}
                stroke="#9ca3af"
              />
              <text x={padding.left - 10} y={yScale(tick) + 4} textAnchor="end" fontSize="10" fill="#6b7280">
                {formatNumber(tick)}
              </text>
              <line
                x1={padding.left}
                y1={yScale(tick)}
                x2={padding.left + chartWidth}
                y2={yScale(tick)}
                stroke="#f3f4f6"
                strokeDasharray="2,2"
              />
            </g>
          ))}

          {/* X-axis */}
          <line
            x1={padding.left}
            y1={padding.top + chartHeight}
            x2={padding.left + chartWidth}
            y2={padding.top + chartHeight}
            stroke="#e5e7eb"
          />
          {data.slice(-7).map((d, i) => {
            const x = xScale(new Date(d.date));
            return (
              <text key={i} x={x} y={height - 10} textAnchor="middle" fontSize="10" fill="#6b7280">
                {new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </text>
            );
          })}

          {/* Lines for each enabled bot */}
          {Object.entries(botData).map(([botName, points]) => {
            if (!enabledBots.has(botName)) return null;
            const path = generatePath(points);
            if (!path) return null;
            return (
              <path
                key={botName}
                d={path}
                fill="none"
                stroke={getBotColor(botName)}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            );
          })}

          {/* Data points */}
          {Object.entries(botData).map(([botName, points]) => {
            if (!enabledBots.has(botName)) return null;
            return points.map((p, i) => (
              <circle
                key={`${botName}-${i}`}
                cx={xScale(new Date(p.date))}
                cy={yScale(p.requests)}
                r="3"
                fill={getBotColor(botName)}
              >
                <title>{`${botName}: ${formatNumber(p.requests)} requests on ${new Date(p.date).toLocaleDateString()}`}</title>
              </circle>
            ));
          })}
        </svg>
      </div>
    );
  };

  // Human vs Bot Comparison Chart
  const HumanVsBotChart = ({ data }: { data: TimeSeriesPoint[] }) => {
    const width = 800;
    const height = 250;
    const padding = { top: 20, right: 20, bottom: 40, left: 70 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    const maxValue = Math.max(...data.map(d => Math.max(d.human_requests, d.bot_requests)));
    const minDate = Math.min(...data.map(d => new Date(d.date).getTime()));
    const maxDate = Math.max(...data.map(d => new Date(d.date).getTime()));

    const xScale = (date: Date) => {
      const range = maxDate - minDate || 1;
      return padding.left + ((date.getTime() - minDate) / range) * chartWidth;
    };

    const yScale = (value: number) => {
      return padding.top + chartHeight - (value / maxValue) * chartHeight;
    };

    const generatePath = (getValue: (d: TimeSeriesPoint) => number) => {
      const sorted = [...data].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      return sorted.map((d, i) => {
        const x = xScale(new Date(d.date));
        const y = yScale(getValue(d));
        return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
      }).join(' ');
    };

    const humanPath = generatePath(d => d.human_requests);
    const botPath = generatePath(d => d.bot_requests);

    // Y-axis ticks
    const yTicks = [0, 0.25, 0.5, 0.75, 1].map(p => Math.round(maxValue * p));

    return (
      <div className="w-full overflow-x-auto">
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ minWidth: '500px' }}>
          {/* Grid and axes */}
          <line x1={padding.left} y1={padding.top} x2={padding.left} y2={padding.top + chartHeight} stroke="#e5e7eb" />
          {yTicks.map((tick, i) => (
            <g key={i}>
              <text x={padding.left - 10} y={yScale(tick) + 4} textAnchor="end" fontSize="10" fill="#6b7280">
                {formatNumber(tick)}
              </text>
              <line
                x1={padding.left}
                y1={yScale(tick)}
                x2={padding.left + chartWidth}
                y2={yScale(tick)}
                stroke="#f3f4f6"
                strokeDasharray="2,2"
              />
            </g>
          ))}

          {/* X-axis dates */}
          {data.filter((_, i) => i % Math.ceil(data.length / 7) === 0).map((d, i) => (
            <text key={i} x={xScale(new Date(d.date))} y={height - 10} textAnchor="middle" fontSize="10" fill="#6b7280">
              {new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </text>
          ))}

          {/* Human line (purple) */}
          <path d={humanPath} fill="none" stroke="#8B5CF6" strokeWidth="2.5" />

          {/* Bot line (orange) */}
          <path d={botPath} fill="none" stroke="#F97316" strokeWidth="2.5" />

          {/* Data points with tooltips */}
          {data.map((d, i) => (
            <g key={i}>
              <circle cx={xScale(new Date(d.date))} cy={yScale(d.human_requests)} r="4" fill="#8B5CF6">
                <title>{`Human: ${formatNumber(d.human_requests)} on ${new Date(d.date).toLocaleDateString()}`}</title>
              </circle>
              <circle cx={xScale(new Date(d.date))} cy={yScale(d.bot_requests)} r="4" fill="#F97316">
                <title>{`Bot: ${formatNumber(d.bot_requests)} on ${new Date(d.date).toLocaleDateString()}`}</title>
              </circle>
            </g>
          ))}
        </svg>

        {/* Legend */}
        <div className="flex justify-center gap-6 mt-2">
          <div className="flex items-center gap-2">
            <div className="w-4 h-1 bg-purple-500 rounded" />
            <span className="text-sm text-muted-foreground">Human Traffic</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-1 bg-orange-500 rounded" />
            <span className="text-sm text-muted-foreground">Bot Traffic</span>
          </div>
        </div>
      </div>
    );
  };

  if (!selectedSite) {
    return (
      <div className="p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please select a site from the header to view crawl intelligence.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Crawl Intelligence</h1>
        <p className="text-muted-foreground">
          Bot traffic analysis, bandwidth usage, and AEO content exposure metrics
        </p>
      </div>

      {/* What the rows in this 30-day window CONTAIN (estate-wide capture under one site, a mixed
          window, ...): the backend's notes, over every tab (task-fm66ezuxve) */}
      <CaptureScopeNotesBanner meta={captureScopeNotes?.siteId === selectedSite.id ? captureScopeNotes.meta : undefined} />

      {/* a panel whose request failed is said so, for THIS site; its data above is empty, never the previous site's */}
      {panelErrors?.siteId === selectedSite.id && (panelErrors.funnel || panelErrors.analytics) && (
        <Alert data-testid="crawl-panels-unavailable">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Some panels could not be loaded for this site</AlertTitle>
          <AlertDescription>
            <ul className="mt-1 space-y-1">
              {panelErrors.funnel && <li>Funnel: {panelErrors.funnel}</li>}
              {panelErrors.analytics && <li>Analytics: {panelErrors.analytics}</li>}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="organic" className="flex items-center gap-1">
            <Sparkles className="h-4 w-4" />
            Organic Traffic
          </TabsTrigger>
          <TabsTrigger value="traffic">Traffic Comparison</TabsTrigger>
          <TabsTrigger value="bots">Bot Analysis</TabsTrigger>
          <TabsTrigger value="urls">Top URLs</TabsTrigger>
          <TabsTrigger value="frontier">Frontier</TabsTrigger>
          <TabsTrigger value="visibility" className="flex items-center gap-1">
            <Eye className="h-4 w-4" />
            AI Visibility
          </TabsTrigger>
          <TabsTrigger value="aeo" className="flex items-center gap-1">
            <Target className="h-4 w-4" />
            AEO Priorities
          </TabsTrigger>
          {(productAvailable === true || availabilityError !== null) && (
            <TabsTrigger value="bots-diario">Bots (daily)</TabsTrigger>
          )}
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {loading ? (
            <div className="grid grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <Card key={i}>
                  <CardHeader className="pb-2">
                    <Skeleton className="h-4 w-24" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-8 w-16" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : funnel ? (
            <>
              {/* Key Metrics */}
              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Bot className="h-4 w-4 text-orange-500" />
                      Bot Requests
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{formatNumber(funnel.total_bot_requests)}</div>
                    <p className="text-xs text-muted-foreground">Total crawl requests</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Users className="h-4 w-4 text-purple-500" />
                      Human Visits
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{formatNumber(funnel.total_human_requests)}</div>
                    <p className="text-xs text-muted-foreground">Total page visits</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <FileCheck className="h-4 w-4 text-blue-500" />
                      AEO Content
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{formatNumber(funnel.potential_urls)}</div>
                    <p className="text-xs text-muted-foreground">URLs with generated content</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-green-500" />
                      Deployed
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{formatNumber(funnel.deployed_urls)}</div>
                    <p className="text-xs text-muted-foreground">{funnel.deployment_rate}% deployment rate</p>
                  </CardContent>
                </Card>
              </div>

              {/* Bandwidth with Inline Bars */}
              {analytics && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <HardDrive className="h-5 w-5" />
                      Bandwidth Served to Bots
                    </CardTitle>
                    <CardDescription>
                      Total: {formatBytes(analytics.bot_breakdown.reduce((sum, b) => sum + b.bytes, 0))}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {analytics.bot_breakdown.slice(0, 8).map((bot) => {
                        const percentage = (bot.bytes / maxBandwidth) * 100;
                        return (
                          <div key={bot.bot_name} className="space-y-1">
                            <div className="flex justify-between text-sm">
                              <span className="font-medium flex items-center gap-2">
                                <div
                                  className="w-3 h-3 rounded-full"
                                  style={{ backgroundColor: getBotColor(bot.bot_name) }}
                                />
                                {bot.bot_name}
                              </span>
                              <span className="text-muted-foreground">{formatBytes(bot.bytes)}</span>
                            </div>
                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all duration-500"
                                style={{
                                  width: `${percentage}%`,
                                  backgroundColor: getBotColor(bot.bot_name)
                                }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Traffic Summary */}
              {analytics && (
                <div className="grid grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <div
                        className="text-sm text-muted-foreground"
                        title="Peak single-run value: MAX(unique_visitors) across log files in the window. Not a window total — actual unique visitors over multiple days may be significantly higher."
                      >Visitors (peak run)</div>
                      <div className="text-2xl font-bold flex items-center gap-1">
                        {formatNumber(analytics.totals.peak_unique_visitors)}
                        {analytics.totals.ip_cap_saturated && (
                          <span
                            className="text-xs font-normal text-amber-600 bg-amber-50 border border-amber-200 rounded px-1"
                            title="Visitor tracking cap reached — peak value is itself a lower bound"
                          >capped</span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div
                        className="text-sm text-muted-foreground"
                        title="Peak single-run value: MAX(unique_urls) across log files in the window. Not a window total — actual unique URLs over multiple days may be significantly higher."
                      >URLs (peak run)</div>
                      <div className="text-2xl font-bold flex items-center gap-1">
                        {formatNumber(analytics.totals.peak_unique_human_urls)}
                        {analytics.totals.url_cap_saturated && (
                          <span
                            className="text-xs font-normal text-amber-600 bg-amber-50 border border-amber-200 rounded px-1"
                            title="URL tracking cap reached — peak value is itself a lower bound"
                          >capped</span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-sm text-muted-foreground">Days with Data</div>
                      <div className="text-2xl font-bold">{analytics.totals.days_with_data}</div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </>
          ) : (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                No crawl data available yet. The Log Analyzer Agent needs to process log files first.
              </AlertDescription>
            </Alert>
          )}
        </TabsContent>

        {/* Traffic Comparison Tab */}
        <TabsContent value="traffic" className="space-y-6">
          {analytics?.time_series && analytics.time_series.length > 0 ? (
            <>
              {/* Human vs Bot Line Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5" />
                    Human vs Bot Traffic Over Time
                  </CardTitle>
                  <CardDescription>Daily request comparison</CardDescription>
                </CardHeader>
                <CardContent>
                  <HumanVsBotChart data={analytics.time_series} />
                </CardContent>
              </Card>

              {/* Bot Type Line Chart with Toggle */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Bot Traffic by Type
                  </CardTitle>
                  <CardDescription>Click bots to show/hide their traffic lines</CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Bot Toggle Buttons */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {analytics.bot_breakdown.slice(0, 10).map(bot => (
                      <Button
                        key={bot.bot_name}
                        variant={enabledBots.has(bot.bot_name) ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => toggleBot(bot.bot_name)}
                        className="gap-2"
                        style={{
                          backgroundColor: enabledBots.has(bot.bot_name) ? getBotColor(bot.bot_name) : undefined,
                          borderColor: getBotColor(bot.bot_name),
                        }}
                      >
                        {enabledBots.has(bot.bot_name) ? (
                          <Eye className="h-3 w-3" />
                        ) : (
                          <EyeOff className="h-3 w-3" />
                        )}
                        {bot.bot_name}
                      </Button>
                    ))}
                  </div>

                  <BotLineChart data={analytics.time_series} bots={analytics.bot_breakdown} />
                </CardContent>
              </Card>

              {/* Daily Breakdown Table */}
              <Card>
                <CardHeader>
                  <CardTitle>Daily Breakdown</CardTitle>
                  <CardDescription>Detailed metrics per day</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Human</TableHead>
                        <TableHead className="text-right">Bot</TableHead>
                        <TableHead className="text-right">Bot/Human Ratio</TableHead>
                        <TableHead className="text-right">Bandwidth</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {analytics.time_series.slice().reverse().slice(0, 10).map((day, i) => {
                        const ratio = day.human_requests > 0
                          ? (day.bot_requests / day.human_requests * 100).toFixed(1)
                          : '0';
                        return (
                          <TableRow key={i}>
                            <TableCell>{new Date(day.date).toLocaleDateString()}</TableCell>
                            <TableCell className="text-right">{formatNumber(day.human_requests)}</TableCell>
                            <TableCell className="text-right">{formatNumber(day.bot_requests)}</TableCell>
                            <TableCell className="text-right text-muted-foreground">{ratio}%</TableCell>
                            <TableCell className="text-right">{formatBytes(day.bot_bytes)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          ) : (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>No time series data available.</AlertDescription>
            </Alert>
          )}
        </TabsContent>

        {/* Bot Analysis Tab */}
        <TabsContent value="bots" className="space-y-4">
          {/* Crawl Behavior Analysis */}
          <Card>
            <CardHeader>
              <CardTitle>Bot Crawl Behavior</CardTitle>
              <CardDescription>
                Crawl frequency, coverage, and efficiency metrics
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-64 w-full" />
              ) : botCrawlStats.length > 0 ? (
                <Table>
                  <TableHeader>
                    {/* the count and ratio headers say which bound they show (PR #5924) */}
                    <BotCrawlTableHeaderRow semantics={analytics?.semantics?.bot_unique_urls} />
                  </TableHeader>
                  <TableBody>
                    {botCrawlStats.map((bot) => (
                      <TableRow key={bot.bot_name}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: getBotColor(bot.bot_name) }}
                            />
                            {bot.bot_name}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{formatNumber(bot.requests)}</TableCell>
                        <BotUniqueUrlsCell bot={bot} />
                        <BotReqPerUrlCell bot={bot} />
                        <TableCell className="text-right">{formatBytes(bot.bytes)}</TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {formatBytes(Math.round(bot.avgBytesPerRequest))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-center text-muted-foreground py-8">No bot data available</p>
              )}
            </CardContent>
          </Card>

          {/* Status Codes */}
          {analytics?.status_codes_by_bot && Object.keys(analytics.status_codes_by_bot).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>HTTP Status Codes by Bot</CardTitle>
                <CardDescription>Response codes returned to each crawler</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {Object.entries(analytics.status_codes_by_bot).slice(0, 8).map(([bot, codes]) => (
                    <div key={bot} className="border rounded-lg p-3">
                      <div className="font-medium mb-2 flex items-center gap-2">
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: getBotColor(bot) }}
                        />
                        {bot}
                      </div>
                      <div className="space-y-1 text-sm">
                        {Object.entries(codes).sort((a, b) => b[1] - a[1]).slice(0, 4).map(([code, count]) => (
                          <div key={code} className="flex justify-between">
                            <Badge variant={code.startsWith('2') ? 'default' : code.startsWith('3') ? 'secondary' : 'destructive'}>
                              {code}
                            </Badge>
                            <span>{formatNumber(count)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Top URLs Tab */}
        <TabsContent value="urls" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Top Visited URLs</CardTitle>
                  <CardDescription>Most trafficked pages by combined bot and human visits</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Badge
                    variant={urlFilter === 'all' ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => setUrlFilter('all')}
                  >
                    All
                  </Badge>
                  <Badge
                    variant={urlFilter === 'potential' ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => setUrlFilter('potential')}
                  >
                    AEO Potential
                  </Badge>
                  <Badge
                    variant={urlFilter === 'deployed' ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => setUrlFilter('deployed')}
                  >
                    Deployed
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>URL</TableHead>
                    <TableHead className="text-right">Bots</TableHead>
                    <TableHead className="text-right">Humans</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">
                      <Clock className="h-4 w-4 inline" /> Last Bot
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {urls && urls.length > 0 ? (
                    urls
                      .slice(0, 50)
                      .map((url, i) => {
                        const botVisits = Number(url.total_bot_visits) || 0;
                        const humanVisits = Number(url.human_visits) || 0;
                        return (
                          <TableRow key={i}>
                            <TableCell className="font-mono text-xs max-w-md truncate" title={url.url_path}>
                              {url.url_path}
                            </TableCell>
                            <TableCell className="text-right">{formatNumber(botVisits)}</TableCell>
                            <TableCell className="text-right">{formatNumber(humanVisits)}</TableCell>
                            <TableCell className="text-right font-medium">
                              {formatNumber(botVisits + humanVisits)}
                            </TableCell>
                            <TableCell className="text-right text-muted-foreground text-sm">
                              {formatTimeAgo(url.last_bot_visit)}
                            </TableCell>
                          </TableRow>
                        );
                      })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground">
                        {urlFilter !== 'all'
                          ? `No ${urlFilter} URLs found. The AEO content may be for products not yet visited.`
                          : 'No URL data available'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Frontier Tab - Story 59 */}
        <TabsContent value="frontier" className="space-y-6">
          {frontierLoading ? (
            <div className="grid grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <Card key={i}>
                  <CardHeader className="pb-2">
                    <Skeleton className="h-4 w-24" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-8 w-16" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : frontier ? (
            <>
              {/* Coverage Metrics */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold">Catalog Coverage</h3>
                  <p className="text-sm text-muted-foreground">
                    Last analysis: {frontier.last_analysis ? formatTimeAgo(frontier.last_analysis) : 'Never'}
                  </p>
                </div>
                <Button onClick={runFrontierAnalysis} disabled={frontierLoading} size="sm">
                  <RefreshCw className={`h-4 w-4 mr-2 ${frontierLoading ? 'animate-spin' : ''}`} />
                  Run Analysis
                </Button>
              </div>

              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Layers className="h-4 w-4 text-blue-500" />
                      Total Products
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{formatNumber(frontier.catalog_coverage.total_products)}</div>
                    <p className="text-xs text-muted-foreground">In catalog</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Target className="h-4 w-4 text-green-500" />
                      Crawled (30d)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{formatNumber(frontier.catalog_coverage.products_crawled_30d)}</div>
                    <p className="text-xs text-muted-foreground">
                      {frontier.catalog_coverage.catalog_coverage_pct}% coverage
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <FileCheck className="h-4 w-4 text-purple-500" />
                      AEO Products
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{formatNumber(frontier.catalog_coverage.aeo_products_total)}</div>
                    <p className="text-xs text-muted-foreground">
                      {frontier.catalog_coverage.aeo_products_crawled} crawled ({frontier.catalog_coverage.aeo_coverage_pct}%)
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 text-orange-500" />
                      Max Depth
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{frontier.depth_analysis.max_crawl_depth}</div>
                    <p className="text-xs text-muted-foreground">
                      Avg: {frontier.depth_analysis.avg_crawl_depth.toFixed(1)} levels
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Coverage Progress Bar */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium">Coverage Progress</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Catalog Coverage</span>
                      <span className="font-medium">{frontier.catalog_coverage.catalog_coverage_pct}%</span>
                    </div>
                    <div className="h-3 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(frontier.catalog_coverage.catalog_coverage_pct, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {formatNumber(frontier.catalog_coverage.products_crawled_30d)} of {formatNumber(frontier.catalog_coverage.total_products)} products crawled
                    </p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>AEO Coverage</span>
                      <span className="font-medium">{frontier.catalog_coverage.aeo_coverage_pct}%</span>
                    </div>
                    <div className="h-3 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-purple-500 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(frontier.catalog_coverage.aeo_coverage_pct, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {formatNumber(frontier.catalog_coverage.aeo_products_crawled)} of {formatNumber(frontier.catalog_coverage.aeo_products_total)} AEO products exposed to bots
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Bot Coverage Section */}
              {botCoverage && (botCoverage.search_bots.length > 0 || botCoverage.ai_bots.length > 0) && (
                <div className="grid grid-cols-2 gap-6">
                  {/* Search Bots */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Globe className="h-4 w-4 text-blue-500" />
                        Search Engine Bots
                      </CardTitle>
                      <CardDescription>Coverage by major search engine crawlers</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {botCoverage.search_bots.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Bot</TableHead>
                              <TableHead className="text-right">Coverage</TableHead>
                              <TableHead className="text-right">Max Depth</TableHead>
                              <TableHead className="text-right">Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {botCoverage.search_bots.map((bot) => (
                              <TableRow key={bot.name}>
                                <TableCell className="font-medium">
                                  <div className="flex items-center gap-2">
                                    <div
                                      className="w-2 h-2 rounded-full"
                                      style={{ backgroundColor: getBotColor(bot.name) }}
                                    />
                                    {bot.display_name}
                                  </div>
                                </TableCell>
                                <TableCell className="text-right">{bot.coverage_pct}%</TableCell>
                                <TableCell className="text-right">{bot.max_depth}</TableCell>
                                <TableCell className="text-right">
                                  <Badge
                                    variant={bot.status === 'healthy' ? 'default' : bot.status === 'warning' ? 'secondary' : 'destructive'}
                                    className={bot.status === 'healthy' ? 'bg-green-100 text-green-800' : ''}
                                  >
                                    {bot.status}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <p className="text-sm text-muted-foreground">No search bot data available</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* AI Bots (AEO Critical) */}
                  <Card className="border-purple-200">
                    <CardHeader>
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Cpu className="h-4 w-4 text-purple-500" />
                        AI Bots (AEO Critical)
                      </CardTitle>
                      <CardDescription>Coverage by AI assistants - critical for Answer Engine Optimization</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {botCoverage.ai_bots.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Bot</TableHead>
                              <TableHead className="text-right">AEO Coverage</TableHead>
                              <TableHead className="text-right">Max Depth</TableHead>
                              <TableHead className="text-right">Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {botCoverage.ai_bots.map((bot) => (
                              <TableRow key={bot.name}>
                                <TableCell className="font-medium">
                                  <div className="flex items-center gap-2">
                                    <div
                                      className="w-2 h-2 rounded-full"
                                      style={{ backgroundColor: getBotColor(bot.name) }}
                                    />
                                    {bot.display_name}
                                  </div>
                                </TableCell>
                                <TableCell className="text-right">
                                  <span className={bot.aeo_coverage_pct < 50 ? 'text-red-600 font-medium' : ''}>
                                    {bot.aeo_coverage_pct}%
                                  </span>
                                </TableCell>
                                <TableCell className="text-right">{bot.max_depth}</TableCell>
                                <TableCell className="text-right">
                                  <Badge
                                    variant={bot.status === 'healthy' ? 'default' : bot.status === 'warning' ? 'secondary' : 'destructive'}
                                    className={bot.status === 'healthy' ? 'bg-green-100 text-green-800' : ''}
                                  >
                                    {bot.status}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Alert>
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>
                            No AI bot crawl data detected. Check if GPTBot, ClaudeBot, and PerplexityBot are allowed in your robots.txt.
                          </AlertDescription>
                        </Alert>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Bot Discovery Insights - Why bots miss content */}
              {(botDepthDropoff.length > 0 || botDiscoveryGaps.length > 0) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Target className="h-4 w-4" />
                      Bot Discovery Insights
                    </CardTitle>
                    <CardDescription>
                      Understand why specific bots miss parts of your catalog. Click a bot to see details.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {botDepthDropoff.map((bot) => {
                        const isExpanded = expandedBots.has(bot.bot_name);
                        const gap = botDiscoveryGaps.find(g => g.bot_name === bot.bot_name);
                        const hasIssue = bot.dropoff_point || bot.max_depth <= 2 || (gap?.total_undiscovered || 0) > 100;

                        return (
                          <div key={bot.bot_name} className={`border rounded-lg overflow-hidden ${hasIssue ? 'border-orange-300 dark:border-orange-700' : 'border-border'}`}>
                            <button
                              onClick={() => {
                                const newExpanded = new Set(expandedBots);
                                if (isExpanded) {
                                  newExpanded.delete(bot.bot_name);
                                } else {
                                  newExpanded.add(bot.bot_name);
                                }
                                setExpandedBots(newExpanded);
                              }}
                              className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <div
                                  className="w-3 h-3 rounded-full"
                                  style={{ backgroundColor: getBotColor(bot.bot_name) }}
                                />
                                <span className="font-medium">{bot.display_name}</span>
                                {hasIssue && (
                                  <Badge variant="secondary" className="bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300">
                                    Issues found
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="text-sm text-muted-foreground">
                                  Max depth: <span className="font-medium">{bot.max_depth}</span>
                                </div>
                                {gap && gap.total_undiscovered > 0 && (
                                  <div className="text-sm text-red-600 dark:text-red-400">
                                    {gap.total_undiscovered.toLocaleString()} URLs missing
                                  </div>
                                )}
                                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              </div>
                            </button>

                            {isExpanded && (
                              <div className="border-t p-4 space-y-4 bg-muted/30">
                                {/* Insight */}
                                <div className="flex items-start gap-2 p-3 bg-background rounded border">
                                  <AlertCircle className="h-4 w-4 mt-0.5 text-blue-500" />
                                  <div>
                                    <p className="text-sm font-medium">Analysis</p>
                                    <p className="text-sm text-muted-foreground">{bot.insight}</p>
                                  </div>
                                </div>

                                {/* Depth Dropoff */}
                                {bot.dropoff_point && (
                                  <div className="p-3 bg-orange-100 dark:bg-orange-950/50 rounded border border-orange-300 dark:border-orange-800">
                                    <p className="text-sm font-medium text-orange-800 dark:text-orange-300 mb-1">Depth Dropoff Detected</p>
                                    <p className="text-sm text-orange-700 dark:text-orange-400">
                                      Crawl drops <span className="font-bold">{bot.dropoff_point.drop_pct}%</span> at depth {bot.dropoff_point.depth}.
                                      Visits went from {bot.dropoff_point.visits_before.toLocaleString()} to {bot.dropoff_point.visits_after.toLocaleString()}.
                                    </p>
                                  </div>
                                )}

                                {/* Depth Distribution mini chart */}
                                {bot.depth_distribution.length > 0 && (
                                  <div>
                                    <p className="text-sm font-medium mb-2">Depth Distribution</p>
                                    <div className="flex items-end gap-1 h-16">
                                      {bot.depth_distribution.map((d) => (
                                        <div
                                          key={d.depth}
                                          className="flex-1 bg-blue-500 rounded-t relative group"
                                          style={{ height: `${Math.max(5, d.pct_of_visits)}%` }}
                                          title={`Depth ${d.depth}: ${d.unique_urls} URLs, ${d.total_visits.toLocaleString()} visits`}
                                        >
                                          <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-xs text-muted-foreground">
                                            {d.depth}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-6 text-center">URL Depth Level</p>
                                  </div>
                                )}

                                {/* Discovery Gaps */}
                                {gap && Object.keys(gap.gaps_by_type).length > 0 && (
                                  <div>
                                    <p className="text-sm font-medium mb-2">Discovery Gaps by Page Type</p>
                                    <div className="grid grid-cols-2 gap-2">
                                      {Object.entries(gap.gaps_by_type).map(([type, data]) => (
                                        <div key={type} className="p-2 bg-muted/50 rounded border">
                                          <div className="flex justify-between items-center">
                                            <span className="text-sm capitalize">{type}</span>
                                            <span className={`text-sm font-medium ${data.gap_pct > 50 ? 'text-red-600 dark:text-red-400' : data.gap_pct > 20 ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'}`}>
                                              {data.gap_pct}% missing
                                            </span>
                                          </div>
                                          <p className="text-xs text-muted-foreground">
                                            {data.undiscovered_urls} of {data.total_urls} URLs
                                          </p>
                                          {data.sample_urls && data.sample_urls.length > 0 && (
                                            <details className="mt-1">
                                              <summary className="text-xs text-blue-600 dark:text-blue-400 cursor-pointer">Show examples</summary>
                                              <ul className="mt-1 text-xs text-muted-foreground">
                                                {data.sample_urls.slice(0, 3).map((url, i) => (
                                                  <li key={i} className="truncate">{url}</li>
                                                ))}
                                              </ul>
                                            </details>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Recommended Actions */}
                                <div className="p-3 bg-blue-100 dark:bg-blue-950/50 rounded border border-blue-300 dark:border-blue-800">
                                  <p className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">Recommended Actions</p>
                                  <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
                                    {bot.max_depth <= 3 && (
                                      <li>• Add internal links from shallow pages to deep content</li>
                                    )}
                                    {bot.dropoff_point && bot.dropoff_point.depth <= 3 && (
                                      <li>• Flatten URL structure - reduce nesting depth</li>
                                    )}
                                    {gap && gap.worst_gap?.type === 'product' && (
                                      <li>• Ensure products are linked from category pages this bot visits</li>
                                    )}
                                    {gap && gap.worst_gap?.type === 'pagination' && (
                                      <li>• Add rel=next/prev pagination hints</li>
                                    )}
                                    <li>• Review and optimize your robots.txt for this crawler</li>
                                  </ul>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Depth Distribution */}
              {Object.keys(frontier.depth_analysis.depth_distribution).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-medium">Crawl Depth Distribution</CardTitle>
                    <CardDescription>Number of URLs and visits by URL depth level</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={Object.entries(frontier.depth_analysis.depth_distribution)
                            .map(([depth, data]) => ({
                              depth: `Depth ${depth}`,
                              urls: data.urls,
                              visits: data.visits
                            }))
                            .sort((a, b) => parseInt(a.depth.split(' ')[1]) - parseInt(b.depth.split(' ')[1]))}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="depth" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="urls" fill="#3b82f6" name="URLs" />
                          <Bar dataKey="visits" fill="#10b981" name="Visits" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Walls Detected */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Crawl Walls Detected</h3>
                {frontier.walls_detected.length > 0 ? (
                  <div className="grid grid-cols-3 gap-4">
                    {frontier.walls_detected.map((wall, i) => (
                      <Card key={i} className={`border-l-4 ${
                        wall.severity === 'critical' ? 'border-l-red-500' :
                        wall.severity === 'high' ? 'border-l-orange-500' : 'border-l-yellow-500'
                      }`}>
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-sm font-medium capitalize flex items-center gap-2">
                              <AlertTriangle className={`h-4 w-4 ${
                                wall.severity === 'critical' ? 'text-red-500' :
                                wall.severity === 'high' ? 'text-orange-500' : 'text-yellow-500'
                              }`} />
                              {wall.type} Wall
                            </CardTitle>
                            <Badge variant={wall.severity === 'critical' ? 'destructive' : 'secondary'}>
                              {wall.severity}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          <p className="text-sm text-muted-foreground">{wall.evidence}</p>
                          <div className="text-sm">
                            <span className="font-medium text-red-600">{formatNumber(wall.products_blocked)}</span>
                            <span className="text-muted-foreground"> products blocked</span>
                          </div>
                          <p className="text-xs text-blue-600 mt-2">{wall.recommendation}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Alert>
                    <Target className="h-4 w-4" />
                    <AlertDescription>
                      No crawl walls detected. Bots appear to be exploring your catalog effectively.
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              {/* Recommendations */}
              {frontier.recommendations.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-medium">Actionable Recommendations</CardTitle>
                    <CardDescription>Click each recommendation to see specific steps you can take</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {frontier.recommendations.map((rec, i) => {
                        const isExpanded = expandedRecs.has(i);
                        const hasActions = rec.actions && rec.actions.length > 0;

                        return (
                          <div key={i} className="border rounded-lg overflow-hidden">
                            <button
                              className={`w-full flex items-start gap-3 p-3 text-left ${hasActions ? 'hover:bg-muted/50 cursor-pointer' : ''} ${isExpanded ? 'bg-muted/30' : ''}`}
                              onClick={() => {
                                if (!hasActions) return;
                                const newExpanded = new Set(expandedRecs);
                                if (isExpanded) {
                                  newExpanded.delete(i);
                                } else {
                                  newExpanded.add(i);
                                }
                                setExpandedRecs(newExpanded);
                              }}
                              disabled={!hasActions}
                            >
                              <Badge variant={rec.impact === 'high' ? 'destructive' : 'secondary'} className="mt-0.5 shrink-0">
                                #{rec.priority}
                              </Badge>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm">{rec.title}</p>
                                <p className="text-xs text-muted-foreground mt-1">{rec.description}</p>
                              </div>
                              {hasActions && (
                                <div className="shrink-0 ml-2">
                                  {isExpanded ? (
                                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                  )}
                                </div>
                              )}
                            </button>

                            {/* Expanded Action Items */}
                            {isExpanded && rec.actions && (
                              <div className="border-t bg-muted/20 p-4 space-y-3">
                                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">How to Fix</p>
                                <div className="space-y-2">
                                  {rec.actions.map((action, j) => (
                                    <div key={j} className="flex items-start gap-3 p-3 bg-white dark:bg-gray-900 rounded border">
                                      <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                                      <div className="flex-1 min-w-0">
                                        <p className="font-medium text-sm">{action.title}</p>
                                        <p className="text-xs text-muted-foreground mt-1">{action.description}</p>
                                        {action.code && (
                                          <code className="block mt-2 p-2 bg-muted rounded text-xs font-mono overflow-x-auto">
                                            {action.code}
                                          </code>
                                        )}
                                        {action.link && (
                                          <a
                                            href={action.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1 mt-2 text-xs text-blue-600 hover:underline"
                                          >
                                            Open tool <ExternalLink className="h-3 w-3" />
                                          </a>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                {/* Sample URLs if available */}
                                {rec.sample_urls && rec.sample_urls.length > 0 && (
                                  <div className="mt-4">
                                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                                      Sample Uncrawled URLs
                                    </p>
                                    <div className="space-y-1">
                                      {rec.sample_urls.slice(0, 5).map((url, k) => (
                                        <code key={k} className="block text-xs text-muted-foreground truncate">
                                          {url}
                                        </code>
                                      ))}
                                      {rec.sample_urls.length > 5 && (
                                        <p className="text-xs text-muted-foreground">
                                          +{rec.sample_urls.length - 5} more...
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <Target className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Frontier Analysis Available</h3>
              <p className="text-muted-foreground mb-4">
                Run an analysis to detect crawl walls and measure catalog coverage.
              </p>
              <Button onClick={runFrontierAnalysis} disabled={frontierLoading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${frontierLoading ? 'animate-spin' : ''}`} />
                Run Analysis
              </Button>
            </div>
          )}
        </TabsContent>

        {/* Organic Traffic Tab */}
        <TabsContent value="organic" className="space-y-6">
          {organicLoading ? (
            <div className="grid grid-cols-3 gap-4">
              {[...Array(3)].map((_, i) => (
                <Card key={i}>
                  <CardHeader className="pb-2">
                    <Skeleton className="h-4 w-24" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-8 w-16" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : organicData ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Search className="h-4 w-4 text-blue-500" />
                      Search Engine Organic
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{formatNumber(organicData.totals.search_total)}</div>
                    <p className="text-xs text-muted-foreground">
                      Google: {formatNumber(organicData.totals.google)} | Bing: {formatNumber(organicData.totals.bing)}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-purple-500" />
                      AI Surface Organic
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{formatNumber(organicData.totals.ai_total)}</div>
                    <p className="text-xs text-muted-foreground">
                      ChatGPT: {formatNumber(organicData.totals.chatgpt)} | Perplexity: {formatNumber(organicData.totals.perplexity)}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-green-500" />
                      AEO Impact
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{organicData.aeo_impact.pct_on_enriched}%</div>
                    <p className="text-xs text-muted-foreground">
                      Organic on {organicData.aeo_impact.deployed_urls} enriched URLs
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Data Freshness Indicator */}
              {organicData.freshness && (organicData.freshness.last_data_date || organicData.freshness.last_updated) && (
                <div className="flex items-center gap-4 text-sm text-muted-foreground bg-muted/30 px-4 py-2 rounded-lg">
                  <Clock className="h-4 w-4" />
                  {organicData.freshness.last_data_date && (
                    <span>
                      Latest data: <span className="font-medium text-foreground">{new Date(organicData.freshness.last_data_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    </span>
                  )}
                  {organicData.freshness.last_updated && (
                    <span className="border-l border-border pl-4">
                      Last sync: <span className="font-medium text-foreground">{new Date(organicData.freshness.last_updated).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    </span>
                  )}
                </div>
              )}

              {/* Organic Traffic Trend Chart */}
              {organicData.daily.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="h-5 w-5" />
                      Organic Traffic Trends
                    </CardTitle>
                    <CardDescription>
                      Daily breakdown of organic traffic from search engines and AI surfaces
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={organicData.daily}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="date"
                          tickFormatter={(value) => {
                            const date = new Date(value);
                            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                          }}
                          interval={Math.max(0, Math.floor(organicData.daily.length / 8) - 1)}
                          tick={{ fontSize: 12 }}
                        />
                        <YAxis tickFormatter={(value) => formatNumber(value)} />
                        <Tooltip
                          formatter={(value: number) => formatNumber(value)}
                          labelFormatter={(label) => new Date(label).toLocaleDateString()}
                        />
                        <Legend />
                        <Area
                          type="monotone"
                          dataKey="search_total"
                          name="Search Organic"
                          stackId="1"
                          stroke="#3b82f6"
                          fill="#3b82f6"
                          fillOpacity={0.6}
                        />
                        <Area
                          type="monotone"
                          dataKey="ai_total"
                          name="AI Organic"
                          stackId="1"
                          stroke="#a855f7"
                          fill="#a855f7"
                          fillOpacity={0.6}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Source Breakdown */}
              <div className="grid grid-cols-2 gap-4">
                {/* Search Engine Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Search className="h-4 w-4" />
                      Search Engine Breakdown
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {[
                        { name: 'Google', value: organicData.totals.google, color: '#4285F4' },
                        { name: 'Bing', value: organicData.totals.bing, color: '#00809D' },
                      ].filter(s => s.value > 0).map((source) => (
                        <div key={source.name} className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: source.color }} />
                          <span className="text-sm flex-1">{source.name}</span>
                          <span className="text-sm font-medium">{formatNumber(source.value)}</span>
                        </div>
                      ))}
                      {organicData.totals.search_total === 0 && (
                        <p className="text-sm text-muted-foreground">No search engine traffic recorded yet</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* AI Surface Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Sparkles className="h-4 w-4" />
                      AI Surface Breakdown
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {[
                        { name: 'ChatGPT', value: organicData.totals.chatgpt, color: '#10A37F' },
                        { name: 'Perplexity', value: organicData.totals.perplexity, color: '#1F8ECD' },
                        { name: 'Claude', value: organicData.totals.claude, color: '#D97706' },
                      ].filter(s => s.value > 0).map((source) => (
                        <div key={source.name} className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: source.color }} />
                          <span className="text-sm flex-1">{source.name}</span>
                          <span className="text-sm font-medium">{formatNumber(source.value)}</span>
                        </div>
                      ))}
                      {organicData.totals.ai_total === 0 && (
                        <p className="text-sm text-muted-foreground">No AI surface traffic recorded yet</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* AEO Impact Details */}
              {organicData.aeo_impact.deployed_urls > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-green-500" />
                      AEO ROI: Organic Traffic on Enriched Pages
                    </CardTitle>
                    <CardDescription>
                      Measuring organic traffic specifically on pages with deployed AEO content
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-muted/50 rounded-lg">
                        <div className="text-2xl font-bold">{formatNumber(organicData.aeo_impact.total_organic)}</div>
                        <p className="text-sm text-muted-foreground">Total Organic</p>
                      </div>
                      <div className="text-center p-4 bg-green-500/10 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">{formatNumber(organicData.aeo_impact.deployed_organic)}</div>
                        <p className="text-sm text-muted-foreground">On Enriched URLs</p>
                      </div>
                      <div className="text-center p-4 bg-blue-500/10 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">{organicData.aeo_impact.pct_on_enriched}%</div>
                        <p className="text-sm text-muted-foreground">Impact Rate</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <Sparkles className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Organic Traffic Data</h3>
              <p className="text-muted-foreground">
                Organic traffic data will appear once log processing captures referrer information.
              </p>
            </div>
          )}
        </TabsContent>

        {/* AI Visibility Tab (Story 63) */}
        <TabsContent value="visibility" className="space-y-6">
          <VisibilityDiagnosisTab />
        </TabsContent>

        {/* AEO Priorities Tab */}
        <TabsContent value="aeo" className="space-y-6">
          <AeoPrioritiesTab />
        </TabsContent>

        {/* Bots diario (3A attribution) — gated by impact-products availability */}
        {(productAvailable === true || availabilityError !== null) && (
          <TabsContent value="bots-diario" className="space-y-4">
            {availabilityError !== null ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{availabilityError}</AlertDescription>
              </Alert>
            ) : (
              <>
                {botsLoading && (
                  <div className="grid grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => (
                      <Card key={i}>
                        <CardHeader className="pb-2"><Skeleton className="h-4 w-24" /></CardHeader>
                        <CardContent><Skeleton className="h-8 w-16" /></CardContent>
                      </Card>
                    ))}
                  </div>
                )}
                {botsError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{botsError}</AlertDescription>
                  </Alert>
                )}
                {!botsLoading && !botsError && !botsData && (
                  <Alert>
                    <AlertDescription>
                      Click &ldquo;Bots (daily)&rdquo; to load the crawl data.
                    </AlertDescription>
                  </Alert>
                )}
                {botsData && <BotsDiarioTab data={botsData} />}
              </>
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
